import { useStore } from "react-redux";
import { dateToString } from "../../utils/dateToString";
import { AxiosInstance } from "axios";
import {
  AddCompanyResponseService,
  UpdateCompanyInputService,
  UpdateCompanyResponseService,
  GetCompanyOfficesResponseService,
  getProfileResponseService,
  BranchOfficeRegister,
  Company_contacts_types,
  CompanyActionsResponse,
} from "./Company.type";
import {
  AddBranchOfficeResponseService,
  BranchInputService,
  UpdateBranchOfficeRequestService,
  UpdateBranchOfficeResponseService,
  DeleteBranchResponseService,
} from "./BranchOffice.type";
import {
  ContactsInputService,
  AddContactsResponseService,
  UpdateContactResponseService,
  UpdateContactInputService,
  GetAllContactsResponseService,
  DeleteContactResponseService,
} from "./Contacts.type";
import {
  AddEmployeeResponseService,
  EmployeeDTO,
  EmployeeInputService,
  GetAllEmployeesResponseService,
  IndividualEmployeeResponseService,
  UpdateEmployeeInputService,
  UpdateEmployeeResponseService,
} from "./Employee.type";
import {
  AddApplicantResponseService,
  ApplicantInputService,
  PromotionApplicantRequestService,
  PromotionApplicantResponseService,
  GetAllApplicantsResponseService,
  GetIndividualApplicant,
} from "./Applicant.type";
import { FormPreValues, FormValues } from "../../pages/others/addCompany/AddCompany.type";
import { Company } from "../user/UserTypes.type";

const bytesFromPayload = (buffer: any): Uint8Array | null => {
  if (!buffer) return null;
  if (typeof buffer === "string") {
    const binary = atob(buffer);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return bytes;
  }
  const list = Array.isArray(buffer) ? buffer : buffer.data;
  if (!Array.isArray(list)) return null;
  return new Uint8Array(list);
};

const fileFromPayload = (payload: any): { blob: Blob; filename: string } => {
  const filename =
    typeof payload?.filename === "string" && payload.filename.trim()
      ? payload.filename.trim()
      : "ResCta.pdf";
  const bytes = bytesFromPayload(payload?.buffer);
  if (!bytes) {
    throw new Error(payload?.message || "No se pudo descargar el archivo");
  }
  return {
    blob: new Blob([bytes], { type: "application/pdf" }),
    filename,
  };
};

const saveBlob = (blob: Blob, filename: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(objectUrl);
};

const resolveCuentaCorrienteFile = async (
  data: any,
): Promise<{ blob: Blob; filename: string }> => {
  if (data instanceof Blob) {
    const type = data.type || "";
    if (type.includes("json") || type.includes("text")) {
      return fileFromPayload(JSON.parse(await data.text()));
    }
    return { blob: data, filename: "ResCta.pdf" };
  }
  return fileFromPayload(data);
};

export default class CompanyServices {
  private axios: AxiosInstance;
  public getStore = useStore();
  constructor(axios: AxiosInstance) {
    this.axios = axios;
  }

  // GET ALL ARTs
  public async getAllArts(): Promise<any> {
    const url: string = `/arts/data`;
    return this.axios.get(url, {
      method: "get",
    });
  }

  // GET HEAD OFFICES WITH FILTER
  public async getAllBranchOfficesFilteringByName(
    companyId: string,
    branchOfficeName: string = "",
  ): Promise<getProfileResponseService[]> {
    const url: string = `/company/${companyId}/branch-office/filters?branchOfficeName=${branchOfficeName}`;
    return this.axios.get(url);
  }

  // CREATE A NEW COMPANY
  public async addCompany(
    data: FormPreValues,
  ): Promise<Company> {

    const url: string = "/company/register/with_users/public";
    return this.axios.post(url, data);
  }

  public async addFileRegister(
    req: any,
    id: string,
  ): Promise<void> {
    const url: string = `/company/${id}/addFileRegister`;
    const formData = new FormData();

    req.file.profileImages.forEach((file: File) => {
      formData.append(`file`, file);
      formData.append(`fileName`, file.name);
    });
    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };
    return this.axios.post(url, formData, config);
  }

  //UPDATE A COMPANY'S INFORMATION
  public async updateCompany(
    data: UpdateCompanyInputService,
  ): Promise<UpdateCompanyResponseService> {
    const { cuit, id, headOfficeId, ...rest } = data;
    const url: string = `/company/${id}/admin`;
    return this.axios.patch(url, rest);
  }

  // GET COMPANY PROFILE DATA
  public async getCompanyProfile(
    companyId?: string,
  ): Promise<getProfileResponseService> {
    //let chosenCompany: any = this.getStore.getState().user.currentCompany.id || this.getStore.getState().user.companies[0].id;
    //console.log(chosenCompany) 

    const url: string = `company/${companyId}/admin/profile`;
    return this.axios.get(url, {
      method: "get",
    });
  }

  // GET HEAD OFFICES WITH FILTER
  public async getAllCompaniesFilteringBySocialReason(
    socialReason: string = "",
  ): Promise<getProfileResponseService[]> {
    const url: string = `/company/head-office/filters?socialReason=${socialReason}`;
    return this.axios.get(url);
  }

  // GET PARENT COMPANY PROFILE
  public async getParentCompanyProfile(): Promise<getProfileResponseService> {
    let chosenCompany: any = this.getStore.getState().user.currentCompany || this.getStore.getState().user.companies[0];
    const company = chosenCompany.companyType !== "Casa Matriz" ? chosenCompany?.headOfficeId : chosenCompany.id
    const url: string = `company/${company}/admin/profile`;
    return this.axios.get(url, {
      method: "get",
    });
  }

  // GET ALL BRANCH OFFICES OF A COMPANY
  public async getCompanyOffices(
    items: number,
    page: number,
  ): Promise<GetCompanyOfficesResponseService> {
    let chosenCompany: any = this.getStore.getState().user.currentCompany || this.getStore.getState().user.companies[0];
    const url: string = `company/${chosenCompany.id}/admin/branch-office?items=${items}&page=${page}`;
    return this.axios.get(url, {
      method: "get",
    });
  }

  // CREATE NEW BRANCH OFFICE
  public async addBranchOffice(data: BranchOfficeRegister, province: string): Promise<BranchOfficeRegister> {
    let chosenCompany: any = this.getStore.getState().user.currentCompany || this.getStore.getState().user.companies[0];
    const url: string = `/company/${chosenCompany.id}/admin/branch-office`;

    const mappedData = {
      branchOffice: {
        ...data.branchOffice,
        email: data.admin.email,
        cuit: data.branchOffice.cuit,
        province: province,
        socialReason: chosenCompany.socialReason,
        fantasyName: chosenCompany.fantasyName,
        ivaCondition: '--'
      },
      admin: {
        ...data.admin,
        email: data.admin.email,
        //name: data.admin.name,
        password: Date.now().toString()
      }
    }
    return this.axios.post(url, mappedData);
  }

  // DELETE BRANCH OFFICE
  public async deleteBranch(id: String): Promise<DeleteBranchResponseService> {
    const url: string = `/company/admin/branch-office/${id}`;
    return this.axios.delete(url);
  }

  // UPDATE BRANCH OFFICE'S INFORMATION
  public async updateBranch(
    data: UpdateBranchOfficeRequestService,
    branchID: String,
  ): Promise<UpdateBranchOfficeResponseService> {
    const url: string = `/company/admin/branch-office/${branchID}`;
    return this.axios.patch(url, data);
  }

  // GET ALL CONTACTS FROM A COMPANY
  // public async getCompanyContacts(
  //   items: number,
  //   page: number,
  // ): Promise<GetAllContactsResponseService> {
  //   let chosenCompany: any = this.getStore.getState().user.currentCompany || this.getStore.getState().user.companies[0];
  //   let id = chosenCompany.id
  //   const url: string = `company/admin/contacts/${id}?items=${items}&page=${page}`;
  //   return this.axios.get(url);
  // }





  // public async getOneContact(contactId: string): Promise<any> {
  //   const url: string = `company/contacts/${contactId}`
  //   return this.axios.get(url)
  // }


  // public async addContacts(
  //   data: ContactsInputService,
  // ): Promise<AddContactsResponseService> {
  //   const { company, ...rest } = data;
  //   const url: string = `/company/${company}/admin/contacts/add`;
  //   return this.axios.post(url, rest);
  // }

  // DELETE SPECIFIC CONTACT
  // public async deleteContact(
  //   id: String,
  // ): Promise<DeleteContactResponseService> {
  //   const url: string = `/company/admin/contacts/${id}`;
  //   return this.axios.delete(url);
  // }

  // UPDATE A SPECIFIC CONTACT'S INFORMATION
  // public async updateContact(
  //   data: UpdateContactInputService,
  // ): Promise<UpdateContactResponseService> {
  //   const { companyId, id, userId, ...rest } = data;
  //   const url: string = `/company/contacts/${id}`;
  //   return this.axios.patch(url, rest);
  // }

  // GET ALL EMPLOYEES FROM A COMPANY
  public async getAllEmployees(): Promise<GetAllEmployeesResponseService> {
    const url: string = `/company/admin/employees/excel`;
    return this.axios.get(url, {
      method: "get",
    });
  }

  // GET ALL EMPLOYEES OF CURRENT COMPANIES
  public async getEmployeesOfCurrentCompany(id: string, signal?: AbortSignal): Promise<IndividualEmployeeResponseService[]> {
    const url: string = `/company/employees/filters?companyId=${id}`;
    return this.axios.get(url, {
      method: "get",
      signal
    });
  }
  // GET ALL EMPLOYEES OF CURRENT COMPANIES
  public async getEmployeesOfCurrentCompanyPagination(
    id: string,
    signal?: AbortSignal,
    itemsPerPage?: number,
    page?: number,
    searchQuery?: string
  ): Promise<IndividualEmployeeResponseService[]> {
    const url: string = `/company/employees/filtersPagination?companyId=${id}&itemsPerPage=${itemsPerPage}&page=${page}&query=${searchQuery}`;
    return this.axios.get(url, {
      method: "get",
      signal
    });

  }

  // GET EMPLOYEE/S BY NAME
  public async getEmployeeWithQuery(
    id?: string,
    query?: string,
    signal?: AbortSignal,
  ): Promise<IndividualEmployeeResponseService[]> {
    const url: string = `/company/employees/query?companyId=${id}&query=${query}`;
    return this.axios.get(url, {
      method: "get",
      signal
    });
  }

  public async updateEmployeePartialData(body: EmployeeDTO) {
    const url: string = `/company/employees/updatePartialData`;
    return this.axios.patch(url, body);
  }

  // GET ALL CANDIDATES
  public async getCandidatesOfCurrentCompany(id: string, signal?: AbortSignal) {
    const url: string = `company/candidates/filters?companyId=${id}`;
    return this.axios.get(url, {
      method: "get",
      signal
    });
  }

  public async getCandidatesWithQuery(query: string, signal?: AbortSignal) {
    const url: string = `company/candidates/query?query=${query}`;
    return this.axios.get(url, {
      method: "get",
      signal
    });
  }

  public async getCandidatesOfCurrentCompanyPagination(
    id: string,
    signal?: AbortSignal,
    itemsPerPage?: number,
    page?: number,
    searchQuery?: string
  ): Promise<GetIndividualApplicant[]> {
    const url: string = `/company/candidates/filtersPagination?companyId=${id}&itemsPerPage=${itemsPerPage}&page=${page}&query=${searchQuery}`;
    return this.axios.get(url, {
      method: "get",
      signal
    });
  }

  public async getOneCompany(id?: string): Promise<getProfileResponseService> {
    const url = `/company/${id}/profile`;
    return this.axios.get(url);
  }

  // GET AN INDIVIDUAL EMPLOYEES PROFILE
  public async getIndividualEmployee(
    employeeID: string,
  ): Promise<IndividualEmployeeResponseService> {
    const url: string = `/company/admin/employees/${employeeID}`;
    return this.axios.get(url, {
      method: "get",
    });
  }

  //CREATE MULTIPLE EMPLOYEES FOR A COMPANY
  public async addMultipleEmployees(
    companyId: string,
    formData: any,
  ): Promise<void> {
    const url: string = `/company/${companyId}/admin/employee/upload`;
    const config = {
      headers: {
        "content-type": "multipart/form-data",
        // "enctype": "multipart/form-data",
      },
    };

    return this.axios.post(url, formData, config);
  }

  //CREATE MULTIPLE APLICANTS FOR A COMPANY
  public async addMultipleAplicants(
    companyId: string,
    formData: any,
  ): Promise<void> {
    const url: string = `/company/${companyId}/admin/aplicant/upload`;
    const config = {
      headers: {
        "content-type": "multipart/form-data",
        // "enctype": "multipart/form-data",
      },
    };

    return this.axios.post(url, formData, config);
  }

  // CREATE NEW EMPLOYEE FOR A COMPANY
  public async addEmployee(
    data: EmployeeInputService,
  ): Promise<AddEmployeeResponseService> {
    //console.log(data);
    const { company, ...rest } = data;
    const url: string = `/company/${company}/admin/employee/add`;
    const req = { ...rest };
    return this.axios.post(url, req);
  }

  // DELETE SPECIFIC EMPLOYEE FROM A COMPANY
  public async deleteEmployee(id: String): Promise<void> {
    const url: string = `/company/admin/employees/${id}`;
    return this.axios.delete(url);
  }

  // GET ALL APPLICANTS (INDIVIDUALS WHO APPLY TO A CERTAIN COMPANY)
  public async getAllEmployeesQuery(query: string): Promise<any[]> {
    const url: string = `/company/admin/employees/multiplerequest`;

    // Si hay una query, la añade a los parámetros de búsqueda
    return query.length > 0
      ? this.axios.get(url, { params: { search: query } }) // Agrega 'query' como parámetro de búsqueda
      : this.axios.get(url); // Si no hay query, realiza la solicitud sin parámetros
  }

  // UPDATE A SPECIFIC EMPLOYEE DATA
  public async updateEmployee(
    data: UpdateEmployeeInputService,
    employeeID: string,
  ): Promise<UpdateEmployeeResponseService> {
    const url: string = `/company/admin/employees/${employeeID}`;
    return this.axios.patch(url, data);
  }

  //EDIT EMPLOYEE EMAIL AND PHONENUMBER
  public async updateEmployeeData(candidateId: string, candidateUserId: string, body: { email: string, phoneNumber: string, name: string, lastname: string }) {
    const url: string = `/company/admin/employees/emailPhone/${candidateId}/${candidateUserId}`;
    return this.axios.patch(url, body);
  }

  // GET ALL APPLICANTS (INDIVIDUALS WHO APPLY TO A CERTAIN COMPANY)
  public async getAllApplicants(): Promise<any[]> {
    const url: string = `/company/admin/candidates/excel`;
    return this.axios.get(url);
  }

  // GET ALL APPLICANTS (INDIVIDUALS WHO APPLY TO A CERTAIN COMPANY)
  public async getAllApplicantsQuery(query: string): Promise<any[]> {
    const url: string = `/company/admin/candidates/multiplerequest`;

    // Si hay una query, la añade a los parámetros de búsqueda
    return query.length > 0
      ? this.axios.get(url, { params: { search: query } }) // Agrega 'query' como parámetro de búsqueda
      : this.axios.get(url); // Si no hay query, realiza la solicitud sin parámetros
  }

  // GET INDIVIDUAL APPLICANT INFO
  public async getIndividualApplicant(
    applicantID: string,
  ): Promise<GetIndividualApplicant> {
    const url: string = `/company/admin/candidates/${applicantID}`;
    return this.axios.get(url);
  }

  // CREATE A NEW APPLICANT FOR A COMPANY
  public async addApplicant(
    data: ApplicantInputService,
  ): Promise<AddApplicantResponseService> {
    const { company, birthDate, ...rest } = data;
    const url: string = `/company/${company}/admin/candidate/add`;
    const req = { ...rest, birthDate: dateToString(birthDate) };
    return this.axios.post(url, req);
  }

  // EDIT AN EXISTING APPLICANT
  public async updateApplicant(
    data: ApplicantInputService,
    applicantID: string,
  ): Promise<void> {
    const url: string = `/company/admin/candidates/${applicantID}`;
    return this.axios.patch(url, data);
  }

  //EDIT APLICANT EMAIL AND PHONENUMBER
  public async updateApplicantData(candidateId: string, candidateUserId: string, body: { email: string, phoneNumber: string, name: string, lastname: string }) {
    const url: string = `/company/admin/candidates/emailPhone/${candidateId}/${candidateUserId}`;
    return this.axios.patch(url, body);
  }

  //PROMOTE AN APPLICANT TO EMPLOYEE
  public async promoteApplicant(
    req: PromotionApplicantRequestService,
  ): Promise<PromotionApplicantResponseService> {
    const url: string = "/company/admin/candidate/promote";
    return this.axios.post(url, req);
  }

  // DELETE SPECIFIC APPLICANT FROM A COMPANY
  public async deleteApplicant(id: String): Promise<void> {
    const url: string = `company/admin/candidates/${id}`;
    return this.axios.delete(url);
  }

  //COMPANY EMAILS SERVICES
  public async updateCompanyEmails(id: string, body: any) {
    const url = `/company/emails/${id}`;
    return this.axios.post(url, body);
  }

  public async getCompanyEmails(code: string) {
    const url = `/company/emails/${code}`;
    return this.axios.get(url);
  }

  public async getStats(companyId: string, signal?: AbortSignal): Promise<any> {
    const url: string = `/company/${companyId}/get-stats`;
    return this.axios.get(url, {
      method: "get",
      signal
    });
  }

  public async getCompanyCuentaCorriente(code: string, signal?: AbortSignal): Promise<any> {
    const url: string = `/company/company_code/${code}/stats`;
    return this.axios.get(url, {
      method: "get",
      signal,
    });
  }

  public async getCuentaCorrientePorFechas(
    idClienteProveedor: string,
    fechaDesde: string,
    fechaHasta: string,
    signal?: AbortSignal,
  ): Promise<any> {
    const url = `/company/cuenta-corriente/por-fechas`;
    return this.axios.get(url, {
      params: { idClienteProveedor, fechaDesde, fechaHasta },
      signal,
    });
  }

  public async downloadCompanyCuentaCorriente(code: string): Promise<void> {
    const url: string = `/company/company_code/${code}/download`;
    const data = await this.axios.get(url, {
      responseType: "blob",
      timeout: 120000,
    });
    const file = await resolveCuentaCorrienteFile(data);
    saveBlob(file.blob, file.filename);
  }

  public async downloadComprobanteFactura(idComprobante: string | number): Promise<void> {
    if (idComprobante == null || idComprobante === "" || idComprobante === "---") {
      throw new Error("Falta el ID de comprobante");
    }

    const url = `/company/comprobante/${idComprobante}/download`;
    // AxiosFactory unwraps response.data after transformResponse runs.
    const data = await this.axios.get<Blob, { body: Blob; filename: string }>(url, {
      responseType: "blob",
      timeout: 120000,
      transformResponse: [
        (body: Blob, headers?: Record<string, string>) => {
          const disposition = headers?.["content-disposition"] ?? "";
          const match = /filename="([^"]+)"/i.exec(disposition);
          const filename = match?.[1]?.trim() || `comprobante-${idComprobante}.pdf`;
          return { body, filename };
        },
      ],
    });

    const blob = data?.body instanceof Blob ? data.body : data;
    const filename =
      typeof data?.filename === "string" && data.filename.trim()
        ? data.filename.trim()
        : `comprobante-${idComprobante}.pdf`;

    if (!(blob instanceof Blob)) {
      throw new Error("No se pudo descargar la factura");
    }

    if ((blob.type || "").includes("json")) {
      const payload = JSON.parse(await blob.text());
      throw new Error(
        payload?.message || payload?.ErrMessage || "No se pudo descargar la factura",
      );
    }

    saveBlob(blob, filename);
  }


  ///NEW COMPANY CONTACT PATHS
  public async findCompanyContacts(id: any): Promise<Company_contacts_types[]> {
    const url = `/company-contacts?company_id=${id}`;
    return this.axios.get(url);
  }

  // CREATE A NEW CONTACT FOR A COMPANY
  public async addContacts(
    data: ContactsInputService,
  ) {
    const { company, ...rest } = data;
    const url: string = `/company-contacts`;
    return this.axios.post(url, rest);
  }


  public async getOneContact(contactId: string): Promise<Company_contacts_types> {
    const url: string = `/company-contacts/individual?contact_id=${contactId}`
    return this.axios.get(url)
  }

  public async updateContact(data: any) {
    const url: string = `/company-contacts`;
    return this.axios.patch(url, data);
  }

  public async deleteContact(id: any) {
    const url = `/company-contacts?contact_id=${id}`;
    return this.axios.delete(url);
  }


  /**POLITICAS DE FACTURACION */

  public async acceptPortalBillingTerms(
    companyId: string,
  ): Promise<{ accepted_portal_billing_terms: boolean; accepted_portal_billing_terms_at?: string }> {
    const url = `/company/${companyId}/admin/accept-portal-terms`;
    return this.axios.patch(url);
  }


  /**MOVIMIENTOS COMERCIALES */


  public async getCompanySpecificActions(
    companyId: string,
  ): Promise<CompanyActionsResponse[]> {
    const url = `/company/${companyId}/admin/movimientos-especificos`;
    return this.axios.get(url);
  }



}
